import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const AvatarMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    try {
      logout();
      await new Promise(resolve => setTimeout(resolve, 100));
      window.location.href = '/login'; // Forzar reload completo
    } catch (error) {

      window.location.href = '/login';
    }
  };

  const menuItems = [
    { label: 'Mi Perfil', icon: '👤', action: () => { navigate('/profile'); setIsOpen(false); } },
  ];

  if (user?.role === 'Presidencia') {
    menuItems.push(
      { label: 'Administración', icon: '⚙️', divider: true },
      { label: 'Usuarios', icon: '👥', action: () => { navigate('/admin/users'); setIsOpen(false); } },
      { label: 'Tags', icon: '🏷️', action: () => { navigate('/admin/tags'); setIsOpen(false); } }
    );
  }

  menuItems.push(
    { label: 'Cerrar Sesión', icon: '🚪', action: () => { handleLogout(); setIsOpen(false); }, divider: true, danger: true }
  );

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="avatar-menu-button"
        className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold hover:bg-primary/30 transition-all"
      >
        {user?.name?.charAt(0).toUpperCase()}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            transition={{ type: 'spring', damping: 20 }}
            className="absolute right-0 top-12 w-64 decko-card shadow-2xl overflow-hidden z-50"
            style={{
              boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(16,185,129,0.1)'
            }}
          >
            {/* User Info con gradiente */}
            <div className="px-4 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-info/5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center text-white font-bold shadow-lg"
                     style={{ boxShadow: '0 0 20px rgba(16,185,129,0.4)' }}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <div className="inline-flex px-3 py-1 bg-primary/10 border border-primary/30 rounded-full">
                <p className="text-xs text-primary font-bold">{user?.role}</p>
              </div>
            </div>

            {/* Menu Items con animaciones */}
            <div className="py-2">
              {menuItems.map((item, index) => (
                <React.Fragment key={index}>
                  {item.divider && <div className="my-2 border-t border-border" />}
                  {item.label !== 'Administración' ? (
                    <motion.button
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      whileHover={{ x: 4, scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (item.action) {
                          item.action();
                        }
                      }}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-all text-left ${
                        item.danger 
                          ? 'text-destructive hover:bg-destructive/5' 
                          : 'text-foreground hover:bg-gradient-to-r hover:from-primary/5 hover:to-transparent'
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-sm font-semibold">{item.label}</span>
                    </motion.button>
                  ) : (
                    <div className="px-4 py-2 text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-info uppercase tracking-wide">
                      {item.label}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AvatarMenu;
